import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, Copy, Eye, Settings2, X,
  Type, AlignLeft, Hash, Mail, Phone, Calendar, Clock, ChevronDown, ListChecks,
  CircleDot, CheckSquare, Paperclip, Files, Image, PenTool, MapPinned, Globe, Building2,
  GitBranch, Loader2, Link2,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi, formsApi } from "@/lib/api";
import {
  FIELD_GROUPS, FIELD_LABELS, makeField, hasOptions, isFileType, isMultiFile,
  OPTION_TYPES, TEXT_LIKE, ALLOWED_FILE_EXTS, uid,
} from "@/lib/formFields";

const ICONS = {
  Type, AlignLeft, Hash, Mail, Phone, Calendar, Clock, ChevronDown, ListChecks,
  CircleDot, CheckSquare, Paperclip, Files, Image, PenTool, MapPinned, Globe, Building2,
};

const input = "w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none transition-colors focus:border-gold focus:ring-2 focus:ring-gold/20";
const lbl = "mb-1 block text-xs font-semibold text-muted-foreground";

export default function FormBuilder() {
  const { id, formId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const dragIdx = useRef(null);

  const load = useCallback(async () => {
    try {
      const [ev, f] = await Promise.all([eventsApi.get(id), formsApi.get(formId)]);
      setEvent(ev);
      setForm(f);
    } catch {
      toast.error("Failed to load form");
      navigate(`/events/${id}/forms`);
    }
  }, [id, formId, navigate]);

  useEffect(() => { load(); }, [load]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateField = (fid, patch) =>
    setForm((f) => ({ ...f, fields: f.fields.map((x) => (x.id === fid ? { ...x, ...patch } : x)) }));

  const addField = (type) => {
    const f = makeField({ type, label: FIELD_LABELS[type], options: hasOptions(type) ? ["Option 1", "Option 2"] : [], config: isFileType(type) && type !== "signature" ? { allowed_types: ["JPG", "JPEG", "PNG", "PDF"], max_size_mb: 10, ...(isMultiFile(type) ? { max_files: 5 } : {}) } : {} });
    setForm((prev) => ({ ...prev, fields: [...prev.fields, f] }));
    setSelectedId(f.id);
  };

  const removeField = (fid) => {
    setForm((f) => ({ ...f, fields: f.fields.filter((x) => x.id !== fid) }));
    if (selectedId === fid) setSelectedId(null);
  };

  const duplicateField = (fid) => {
    setForm((f) => {
      const idx = f.fields.findIndex((x) => x.id === fid);
      const copy = { ...f.fields[idx], id: uid(), label: `${f.fields[idx].label} (copy)` };
      const fields = [...f.fields];
      fields.splice(idx + 1, 0, copy);
      return { ...f, fields };
    });
  };

  const onDrop = (targetIdx) => {
    const from = dragIdx.current;
    dragIdx.current = null;
    if (from === null || from === targetIdx) return;
    setForm((f) => {
      const fields = [...f.fields];
      const [m] = fields.splice(from, 1);
      fields.splice(targetIdx, 0, m);
      return { ...f, fields };
    });
  };

  const save = async (nextStatus) => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        form_type: form.form_type,
        description: form.description,
        status: nextStatus || form.status,
        consent_enabled: form.consent_enabled,
        consent_text: form.consent_text,
        notify_on_submit: form.notify_on_submit,
        require_auth: form.require_auth,
        fields: form.fields,
      };
      const saved = await formsApi.update(formId, payload);
      setForm(saved);
      toast.success(nextStatus ? `Form ${nextStatus}` : "Form saved");
    } catch {
      toast.error("Could not save form");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/event/${event.slug}/${form.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied");
  };

  const preview = () => window.open(`/event/${event.slug}/${form.slug}`, "_blank");

  if (!form) {
    return (
      <div className="min-h-screen bg-background"><Navbar />
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
      </div>
    );
  }

  const selected = form.fields.find((x) => x.id === selectedId) || null;
  const otherOptionFields = form.fields.filter((x) => x.id !== selectedId && (hasOptions(x.type)));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/events/${id}/forms`)} className="rounded-xl border border-border p-2 hover:bg-secondary" data-testid="builder-back"><ArrowLeft className="h-4 w-4" /></button>
            <input value={form.name} onChange={(e) => update({ name: e.target.value })} data-testid="builder-form-name" className="min-w-[200px] rounded-xl border border-transparent bg-transparent px-2 py-1.5 font-serif text-2xl font-semibold outline-none hover:border-border focus:border-gold" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={form.status} onChange={(e) => save(e.target.value)} data-testid="builder-status" className="rounded-xl border border-input bg-white px-3 py-2 text-sm font-medium outline-none focus:border-gold">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
            </select>
            <button onClick={preview} data-testid="builder-preview" className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-sm font-medium hover:bg-secondary"><Eye className="h-4 w-4" /> Preview</button>
            <button onClick={copyLink} data-testid="builder-copy-link" className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-sm font-medium hover:bg-secondary"><Link2 className="h-4 w-4" /> Link</button>
            <button onClick={() => setShowSettings(true)} data-testid="builder-settings" className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-sm font-medium hover:bg-secondary"><Settings2 className="h-4 w-4" /> Settings</button>
            <button onClick={() => save()} disabled={saving} data-testid="builder-save" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-[260px_1fr_340px]">
          {/* Palette */}
          <div className="space-y-4" data-testid="field-palette">
            {FIELD_GROUPS.map((g) => (
              <div key={g.group} className="rounded-2xl border border-border bg-card p-4">
                <p className="eyebrow mb-3 text-muted-foreground">{g.group}</p>
                <div className="grid grid-cols-2 gap-2">
                  {g.items.map((it) => {
                    const Icon = ICONS[it.icon] || Type;
                    return (
                      <button key={it.type} onClick={() => addField(it.type)} data-testid={`add-field-${it.type}`} className="flex items-center gap-2 rounded-xl border border-border px-2.5 py-2 text-left text-xs font-medium transition-colors hover:border-gold hover:bg-gold/5">
                        <Icon className="h-3.5 w-3.5 shrink-0 text-gold" /><span className="truncate">{it.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Canvas */}
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="form-canvas">
            {form.fields.length === 0 ? (
              <div className="flex h-60 flex-col items-center justify-center text-center text-sm text-muted-foreground">
                <Plus className="mb-2 h-8 w-8 text-gold" />
                Add fields from the left to build your form.
              </div>
            ) : (
              <div className="space-y-2">
                {form.fields.map((f, idx) => {
                  const active = f.id === selectedId;
                  return (
                    <div
                      key={f.id}
                      draggable
                      onDragStart={() => (dragIdx.current = idx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(idx)}
                      onClick={() => setSelectedId(f.id)}
                      data-testid={`canvas-field-${f.id}`}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${active ? "border-gold bg-gold/5 ring-2 ring-gold/20" : "border-border hover:bg-secondary/50"}`}
                    >
                      <GripVertical className="mt-1 h-4 w-4 shrink-0 cursor-grab text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold">{f.label || "Untitled field"}</span>
                          {f.required && <span className="text-destructive">*</span>}
                          <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{FIELD_LABELS[f.type]}</span>
                          {f.conditional?.field && <GitBranch className="h-3 w-3 text-gold" title="Conditional" />}
                        </div>
                        {f.description && <p className="mt-0.5 truncate text-xs text-muted-foreground">{f.description}</p>}
                        {hasOptions(f.type) && <p className="mt-1 truncate text-xs text-muted-foreground">{(f.options || []).join(" · ")}</p>}
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button onClick={(e) => { e.stopPropagation(); duplicateField(f.id); }} data-testid={`dup-field-${f.id}`} title="Duplicate" className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"><Copy className="h-3.5 w-3.5" /></button>
                        <button onClick={(e) => { e.stopPropagation(); removeField(f.id); }} data-testid={`del-field-${f.id}`} title="Delete" className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Field editor */}
          <div className="rounded-2xl border border-border bg-card p-5" data-testid="field-editor">
            {!selected ? (
              <div className="flex h-60 items-center justify-center text-center text-sm text-muted-foreground">Select a field to edit its settings.</div>
            ) : (
              <div className="space-y-4">
                <p className="eyebrow text-gold">{FIELD_LABELS[selected.type]}</p>
                <div>
                  <label className={lbl}>Field Name</label>
                  <input value={selected.label} onChange={(e) => updateField(selected.id, { label: e.target.value })} className={input} data-testid="fe-label" />
                </div>
                <div>
                  <label className={lbl}>Description / Instructions</label>
                  <input value={selected.description} onChange={(e) => updateField(selected.id, { description: e.target.value })} className={input} data-testid="fe-description" />
                </div>
                {TEXT_LIKE.includes(selected.type) && (
                  <div>
                    <label className={lbl}>Placeholder</label>
                    <input value={selected.placeholder} onChange={(e) => updateField(selected.id, { placeholder: e.target.value })} className={input} data-testid="fe-placeholder" />
                  </div>
                )}

                {/* options */}
                {OPTION_TYPES.includes(selected.type) && (
                  <div>
                    <label className={lbl}>Options</label>
                    <div className="space-y-2">
                      {(selected.options || []).map((o, i) => (
                        <div key={i} className="flex gap-2">
                          <input value={o} onChange={(e) => updateField(selected.id, { options: selected.options.map((x, j) => (j === i ? e.target.value : x)) })} className={input} data-testid={`fe-option-${i}`} />
                          <button onClick={() => updateField(selected.id, { options: selected.options.filter((_, j) => j !== i) })} className="rounded-lg border border-border px-2 text-destructive hover:bg-destructive/10"><X className="h-4 w-4" /></button>
                        </div>
                      ))}
                      <button onClick={() => updateField(selected.id, { options: [...(selected.options || []), `Option ${(selected.options?.length || 0) + 1}`] })} data-testid="fe-add-option" className="inline-flex items-center gap-1 text-sm font-medium text-gold"><Plus className="h-4 w-4" /> Add option</button>
                    </div>
                  </div>
                )}

                {/* number validation */}
                {selected.type === "number" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className={lbl}>Min</label><input type="number" value={selected.config?.min ?? ""} onChange={(e) => updateField(selected.id, { config: { ...selected.config, min: e.target.value === "" ? undefined : Number(e.target.value) } })} className={input} data-testid="fe-min" /></div>
                    <div><label className={lbl}>Max</label><input type="number" value={selected.config?.max ?? ""} onChange={(e) => updateField(selected.id, { config: { ...selected.config, max: e.target.value === "" ? undefined : Number(e.target.value) } })} className={input} data-testid="fe-max" /></div>
                  </div>
                )}
                {(selected.type === "short_text" || selected.type === "long_text") && (
                  <div><label className={lbl}>Max length</label><input type="number" value={selected.config?.maxlength ?? ""} onChange={(e) => updateField(selected.id, { config: { ...selected.config, maxlength: e.target.value === "" ? undefined : Number(e.target.value) } })} className={input} data-testid="fe-maxlength" /></div>
                )}

                {/* file config */}
                {isFileType(selected.type) && selected.type !== "signature" && (
                  <div className="space-y-3 rounded-xl bg-secondary/50 p-3">
                    <p className="text-xs font-semibold">Upload settings</p>
                    {selected.type !== "image" && (
                      <div>
                        <label className={lbl}>Allowed file types</label>
                        <div className="flex flex-wrap gap-1.5">
                          {ALLOWED_FILE_EXTS.map((ext) => {
                            const on = (selected.config?.allowed_types || []).includes(ext);
                            return (
                              <button key={ext} onClick={() => { const cur = selected.config?.allowed_types || []; updateField(selected.id, { config: { ...selected.config, allowed_types: on ? cur.filter((x) => x !== ext) : [...cur, ext] } }); }} data-testid={`fe-ext-${ext}`} className={`rounded-lg border px-2.5 py-1 text-xs font-medium ${on ? "border-gold bg-gold/10 text-gold" : "border-border text-muted-foreground"}`}>{ext}</button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <div><label className={lbl}>Max size (MB)</label><input type="number" value={selected.config?.max_size_mb ?? ""} onChange={(e) => updateField(selected.id, { config: { ...selected.config, max_size_mb: e.target.value === "" ? undefined : Number(e.target.value) } })} className={input} data-testid="fe-maxsize" /></div>
                      {isMultiFile(selected.type) && <div><label className={lbl}>Max files</label><input type="number" value={selected.config?.max_files ?? ""} onChange={(e) => updateField(selected.id, { config: { ...selected.config, max_files: e.target.value === "" ? undefined : Number(e.target.value) } })} className={input} data-testid="fe-maxfiles" /></div>}
                    </div>
                  </div>
                )}

                {/* required */}
                <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border px-3 py-2.5">
                  <span className="text-sm font-medium">Required</span>
                  <input type="checkbox" checked={selected.required} onChange={(e) => updateField(selected.id, { required: e.target.checked })} className="accent-[hsl(var(--gold))]" data-testid="fe-required" />
                </label>

                {/* conditional */}
                <div className="rounded-xl border border-border p-3">
                  <label className="flex cursor-pointer items-center justify-between">
                    <span className="inline-flex items-center gap-1.5 text-sm font-medium"><GitBranch className="h-4 w-4 text-gold" /> Conditional logic</span>
                    <input type="checkbox" checked={!!selected.conditional} onChange={(e) => updateField(selected.id, { conditional: e.target.checked ? { field: otherOptionFields[0]?.id || "", op: "equals", value: "" } : null })} className="accent-[hsl(var(--gold))]" data-testid="fe-conditional-toggle" />
                  </label>
                  {selected.conditional && (
                    <div className="mt-3 space-y-2">
                      {otherOptionFields.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Add a dropdown, radio or checkbox field first to control visibility.</p>
                      ) : (
                        <>
                          <p className="text-xs text-muted-foreground">Show this field only when:</p>
                          <select value={selected.conditional.field} onChange={(e) => updateField(selected.id, { conditional: { ...selected.conditional, field: e.target.value, value: "" } })} className={input} data-testid="fe-cond-field">
                            {otherOptionFields.map((of) => <option key={of.id} value={of.id}>{of.label}</option>)}
                          </select>
                          <select value={selected.conditional.op} onChange={(e) => updateField(selected.id, { conditional: { ...selected.conditional, op: e.target.value } })} className={input} data-testid="fe-cond-op">
                            <option value="equals">is equal to</option>
                            <option value="not_equals">is not equal to</option>
                            <option value="contains">contains</option>
                          </select>
                          {(() => {
                            const ctrl = form.fields.find((x) => x.id === selected.conditional.field);
                            const opts = ctrl?.options || [];
                            return opts.length ? (
                              <select value={selected.conditional.value} onChange={(e) => updateField(selected.id, { conditional: { ...selected.conditional, value: e.target.value } })} className={input} data-testid="fe-cond-value">
                                <option value="">Select value…</option>
                                {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                              </select>
                            ) : (
                              <input value={selected.conditional.value} onChange={(e) => updateField(selected.id, { conditional: { ...selected.conditional, value: e.target.value } })} className={input} placeholder="Value" data-testid="fe-cond-value" />
                            );
                          })()}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Settings modal */}
      {showSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowSettings(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-card p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="form-settings-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold">Form Settings</h2>
              <button onClick={() => setShowSettings(false)} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className={lbl}>Description</label>
                <textarea value={form.description} onChange={(e) => update({ description: e.target.value })} rows={2} className={input} data-testid="settings-description" />
              </div>
              <label className="flex cursor-pointer items-center justify-between rounded-xl border border-border px-3 py-2.5">
                <span className="text-sm font-medium">Email me on new submissions</span>
                <input type="checkbox" checked={form.notify_on_submit} onChange={(e) => update({ notify_on_submit: e.target.checked })} className="accent-[hsl(var(--gold))]" data-testid="settings-notify" />
              </label>
              <div className="rounded-xl border border-border p-3">
                <label className="flex cursor-pointer items-center justify-between">
                  <span className="text-sm font-medium">Require consent checkbox</span>
                  <input type="checkbox" checked={form.consent_enabled} onChange={(e) => update({ consent_enabled: e.target.checked })} className="accent-[hsl(var(--gold))]" data-testid="settings-consent" />
                </label>
                {form.consent_enabled && (
                  <textarea value={form.consent_text} onChange={(e) => update({ consent_text: e.target.value })} rows={3} className={`${input} mt-2`} data-testid="settings-consent-text" />
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button onClick={() => { setShowSettings(false); save(); }} data-testid="settings-save" className="rounded-full bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground">Save settings</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
